/**
 * Utilitaires pour la normalisation et la manipulation des polygones de zones de livraison
 * Format normalisé interne : [{lat: number, lng: number}, ...]
 */

/**
 * Normalise un polygone depuis l'API vers le format interne
 * Gère différents formats possibles :
 * - [{lat, lng}, ...] (format normalisé)
 * - [[lat, lng], ...] (format array de arrays)
 * - String JSON
 * - null/undefined
 * 
 * @param {any} raw - Données brutes depuis l'API
 * @returns {Array<{lat: number, lng: number}>} Polygone normalisé ou tableau vide
 */
export function normalizePolygonFromApi(raw) {
  // Si null, undefined ou vide, retourner tableau vide
  if (!raw) {
    return [];
  }

  // Si c'est déjà un tableau
  if (Array.isArray(raw)) {
    // Vérifier si c'est déjà au format normalisé [{lat, lng}, ...]
    if (raw.length > 0 && typeof raw[0] === 'object' && raw[0] !== null && 'lat' in raw[0] && 'lng' in raw[0]) {
      return raw.map(point => ({
        lat: parseFloat(point.lat),
        lng: parseFloat(point.lng)
      })).filter(point => !isNaN(point.lat) && !isNaN(point.lng));
    }
    
    // Si c'est un format [[lat, lng], ...]
    if (raw.length > 0 && Array.isArray(raw[0]) && raw[0].length >= 2) {
      return raw.map(point => ({
        lat: parseFloat(point[0]),
        lng: parseFloat(point[1])
      })).filter(point => !isNaN(point.lat) && !isNaN(point.lng));
    }
  }

  // Si c'est une string JSON, parser
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return normalizePolygonFromApi(parsed);
    } catch (e) {
      console.warn('Erreur lors du parsing JSON du polygone:', e);
      return [];
    }
  }

  // Format inconnu, retourner tableau vide
  console.warn('Format de polygone non reconnu:', raw);
  return [];
}

/**
 * Sérialise un polygone pour l'envoi à l'API
 * Convertit le format interne [{lat, lng}, ...] en JSON string
 * 
 * @param {Array<{lat: number, lng: number}>} polygon - Polygone au format normalisé
 * @returns {string|null} JSON string ou null si polygone vide
 */
export function serializePolygonForApi(polygon) {
  if (!polygon || !Array.isArray(polygon) || polygon.length === 0) {
    return null;
  }

  // Valider que tous les points ont lat et lng
  const validPolygon = polygon
    .map(point => {
      const lat = parseFloat(point.lat);
      const lng = parseFloat(point.lng);
      if (isNaN(lat) || isNaN(lng)) {
        return null;
      }
      return { lat, lng };
    })
    .filter(point => point !== null);

  if (validPolygon.length === 0) {
    return null;
  }

  // Retourner le JSON stringifié
  return JSON.stringify(validPolygon);
}

/**
 * Vérifie si un polygone est valide (au moins 3 points)
 * 
 * @param {Array<{lat: number, lng: number}>} polygon - Polygone à valider
 * @returns {boolean} True si valide
 */
export function isValidPolygon(polygon) {
  if (!polygon || !Array.isArray(polygon)) {
    return false;
  }
  
  const normalized = normalizePolygonFromApi(polygon);
  return normalized.length >= 3;
}

/**
 * Calcule le centre d'un polygone (centroïde)
 * 
 * @param {Array<{lat: number, lng: number}>} polygon - Polygone
 * @returns {{lat: number, lng: number}|null} Centre du polygone ou null
 */
export function getPolygonCenter(polygon) {
  const normalized = normalizePolygonFromApi(polygon);
  
  if (normalized.length === 0) {
    return null;
  }

  const sum = normalized.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / normalized.length,
    lng: sum.lng / normalized.length
  };
}

/**
 * Convertit un polygone au format Leaflet ([[lat, lng], ...]) vers le format normalisé
 * 
 * @param {Array<Array<number>>} leafletPolygon - Polygone au format Leaflet
 * @returns {Array<{lat: number, lng: number}>} Polygone normalisé
 */
export function fromLeafletFormat(leafletPolygon) {
  if (!Array.isArray(leafletPolygon)) {
    return [];
  }

  return leafletPolygon.map(point => {
    if (Array.isArray(point) && point.length >= 2) {
      return {
        lat: parseFloat(point[0]),
        lng: parseFloat(point[1])
      };
    }
    return null;
  }).filter(point => point !== null && !isNaN(point.lat) && !isNaN(point.lng));
}

/**
 * Convertit un polygone au format normalisé vers le format Leaflet ([[lat, lng], ...])
 * 
 * @param {Array<{lat: number, lng: number}>} polygon - Polygone normalisé
 * @returns {Array<Array<number>>} Polygone au format Leaflet
 */
export function toLeafletFormat(polygon) {
  const normalized = normalizePolygonFromApi(polygon);
  return normalized.map(point => [point.lat, point.lng]);
}




